/**
 * Shared Condition Parsers
 *
 * Consolidated parsing logic for all condition types.
 * Used by both ConditionViewer (for direct conditions) and InlineConditionFetch (for fetched conditions).
 */

import { getOperatorLabel } from '../../utils/segment-mapping';
import { CONDITION_TYPE, type ParsedCondition, type ParsedFilter } from './constants';
import type { DataSourceType } from './data-source-label';
import { DATA_SOURCE_TYPE_ICON } from './data-source-label';
import { isEmptyString } from '@/utils/string';

// =============================================================================
// Constants
// =============================================================================

/**
 * Regex pattern to extract campaign ID from message names like "email-campaign_126" or "email-campaign_126_h"
 * Captures the numeric campaign ID after "-campaign_"
 */
const CAMPAIGN_ID_PATTERN = /-campaign_(\d+)/;

/**
 * Campaign message name suffixes
 * - `_h`: Human opens/clicks (excludes bot activity)
 *   Used in event type names like "email-campaign_126_h" to indicate human interaction events
 * @internal - Defined for documentation purposes, not currently used in code
 */
const _HUMAN_ACTIVITY_SUFFIX = '_h';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a value for display
 */
function formatValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  return JSON.stringify(value);
}

/**
 * Check if an operator is a relative timestamp operator.
 * Relative operators use seconds offset from now (negative = past, positive = future).
 */
function isRelativeTimestampOperator(operator: string | undefined): boolean {
  return (
    operator === 'timestamp_gt' ||
    operator === 'timestamp_lt' ||
    operator === 'after_relative_timestamp' ||
    operator === 'before_relative_timestamp'
  );
}

/**
 * Check if an operator is an absolute timestamp operator.
 * Absolute operators use unix timestamps.
 */
function isAbsoluteTimestampOperator(operator: string | undefined): boolean {
  return (
    operator === 'timestamp_gta' ||
    operator === 'timestamp_lta' ||
    operator === 'timestamp_bw' ||
    operator === 'after_absolute_timestamp' ||
    operator === 'before_absolute_timestamp' ||
    operator === 'between_absolute_timestamps'
  );
}

/**
 * Format a relative timestamp value to human-readable string.
 * Negative values = past (e.g., -86400 = yesterday)
 * Positive values = future (e.g., 86400 = tomorrow)
 * Source: ~/code/ui/app/components/condition-editor-v2/state-utils.ts
 */
function formatRelativeTimestamp(seconds: number): string {
  const isPast = seconds < 0;
  const days = Math.floor(Math.abs(seconds) / SECONDS_PER_DAY);

  if (days === 0) {
    return 'today';
  }

  if (days === 1) {
    return isPast ? 'yesterday' : 'tomorrow';
  }

  const daysText = `${days} ${pluralize(days, 'day', 'days')}`;
  return isPast ? `${daysText} ago` : `${daysText} from now`;
}

/**
 * Format an absolute timestamp (unix timestamp) to human-readable date.
 */
function formatAbsoluteTimestamp(timestamp: number): string {
  try {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(timestamp);
  }
}

/**
 * Extract filters array from various filter structures.
 * Handles: direct array, { and: [...] }, { or: [...] }, or single filter object
 */
function extractFiltersArray(
  filtersData: unknown,
): { filters: Record<string, unknown>[]; operator: 'and' | 'or' } | null {
  if (filtersData == null) return null;

  // Already an array
  if (Array.isArray(filtersData)) {
    return { filters: filtersData as Record<string, unknown>[], operator: 'and' };
  }

  if (typeof filtersData === 'object') {
    const obj = filtersData as Record<string, unknown>;

    // { and: [...] } structure
    if ('and' in obj && Array.isArray(obj.and)) {
      return { filters: obj.and as Record<string, unknown>[], operator: 'and' };
    }

    // { or: [...] } structure
    if ('or' in obj && Array.isArray(obj.or)) {
      return { filters: obj.or as Record<string, unknown>[], operator: 'or' };
    }

    // Single filter object - wrap in array
    if ('field' in obj || 'attribute' in obj || 'operator' in obj) {
      return { filters: [obj], operator: 'and' };
    }
  }

  return null;
}

/**
 * Parse a raw filter object into a ParsedFilter for display.
 * Used to convert backend filter data to the format expected by the UI.
 *
 * Handles multiple formats:
 * - Direct: { field, operator, value }
 * - Nested: { attribute: { field, operator, value } }
 */
function parseFilter(filter: Record<string, unknown>): ParsedFilter | null {
  // Handle nested { attribute: { field, operator, value } } structure from EventType filters
  let filterData = filter;
  if (
    'attribute' in filter &&
    typeof filter.attribute === 'object' &&
    filter.attribute != null &&
    !Array.isArray(filter.attribute)
  ) {
    filterData = filter.attribute as Record<string, unknown>;
  }

  const fieldRaw = (filterData.field ?? filterData.name) as string | undefined;
  const operator = filterData.operator as string | undefined;
  const value = filterData.value;
  const value2 = filterData.value2;
  const inverse = filterData.inverse === true;

  if (fieldRaw == null) return null;

  const field = fieldRaw; // Now TypeScript knows field is string
  const operatorLabel = (operator != null ? getOperatorLabel(operator, inverse) : undefined) ?? 'is';

  return {
    field,
    operator: operatorLabel,
    value: value != null ? formatValue(value) : undefined,
    value2: value2 != null ? formatValue(value2) : undefined,
  };
}

/**
 * Parse an array of raw filters into ParsedFilter objects.
 */
function parseFilters(filters: Record<string, unknown>[]): ParsedFilter[] {
  return filters.map((filter) => parseFilter(filter)).filter((f): f is ParsedFilter => f != null);
}

/**
 * Format a value for display, with special handling for timestamp operators.
 */
function formatValueWithOperator(value: unknown, operator: string | undefined): string {
  if (value == null) return '';

  const numValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : null;

  // Handle relative timestamp operators (yesterday, tomorrow, X days ago, etc.)
  if (isRelativeTimestampOperator(operator) && numValue != null && !Number.isNaN(numValue)) {
    return formatRelativeTimestamp(numValue);
  }

  // Handle absolute timestamp operators (format as date)
  if (isAbsoluteTimestampOperator(operator) && numValue != null && !Number.isNaN(numValue)) {
    return formatAbsoluteTimestamp(numValue);
  }

  return formatValue(value);
}

/**
 * Pluralize a word based on count
 */
function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/**
 * Time constants for duration formatting.
 * Used throughout the file for converting seconds to human-readable durations.
 */
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

/**
 * Format a time duration in seconds to a human-readable string
 */
function formatWithin(seconds: number): string {
  // UI always shows days for "within" values, with "the past" prefix
  // See: ~/code/ui/app/components/condition-editor-v2/parts/within.gts
  if (seconds === 0) return 'ever';

  const days = Math.floor(Math.abs(seconds) / SECONDS_PER_DAY);

  if (days === 0) {
    // Less than a day - show in hours/minutes/seconds
    if (seconds < SECONDS_PER_MINUTE) {
      return `the past ${seconds === 1 ? '1 second' : `${seconds} seconds`}`;
    }
    if (seconds < SECONDS_PER_HOUR) {
      const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
      return `the past ${minutes === 1 ? '1 minute' : `${minutes} minutes`}`;
    }
    const hours = Math.floor(seconds / SECONDS_PER_HOUR);
    return `the past ${hours === 1 ? '1 hour' : `${hours} hours`}`;
  }

  return `the past ${days === 1 ? '1 day' : `${days} days`}`;
}

/**
 * Message channel types and their display labels.
 * Used to generate EVENT_TYPE_TO_LABEL programmatically.
 */
const MESSAGE_CHANNELS: Record<string, string> = {
  email: 'email',
  twilio: 'SMS',
  push: 'push notification',
  in_app: 'in-app message',
  slack: 'Slack message',
  webhook: 'webhook',
  whatsapp: 'WhatsApp message',
  line: 'LINE message',
  urban_airship: 'Airship notification',
  action: 'action',
};

/**
 * Action prefixes that can be combined with channels.
 */
const ACTION_PREFIXES = [
  'sent',
  'delivered',
  'opened',
  'clicked',
  'bounced',
  'dropped',
  'converted',
  'failed',
  'spammed',
  'unsubscribed',
];

/**
 * Generate EVENT_TYPE_TO_LABEL map programmatically.
 * Creates entries like "sent_email" -> "email", "clicked_slack" -> "Slack message"
 */
function generateEventTypeToLabel(): Record<string, string> {
  const map: Record<string, string> = {};

  // Generate all combinations of action_channel
  for (const [channel, label] of Object.entries(MESSAGE_CHANNELS)) {
    for (const prefix of ACTION_PREFIXES) {
      map[`${prefix}_${channel}`] = label;
    }
  }

  // Add other event types
  map.event = 'event';
  map.page = 'page';
  map.screen = 'screen';
  map.pageview = 'page view';
  map.screenview = 'screen view';

  return map;
}

/**
 * Map EventType.type field to human-readable labels for wildcards.
 * When name is "*", we display "any {type label}" instead of just "*".
 * Source: ~/code/ui/app/components/condition-editor-v2/parts/message.gts
 */
const EVENT_TYPE_TO_LABEL = generateEventTypeToLabel();

/**
 * Check if an event name is a wildcard pattern.
 * Wildcards are "*" or end with "*" (e.g., "twilio_*").
 */
function isWildcardName(name: string | null | undefined): boolean {
  return name === '*' || (name != null && name.endsWith('*'));
}

/**
 * Format an event name for display.
 * - For wildcards with known type: "any email", "any SMS", etc.
 * - For wildcards without type: "any event"
 * - For specific names: the name itself
 */
function formatEventName(
  name: string | null | undefined,
  eventType: string | null | undefined,
): { displayName: string; isWildcard: boolean } {
  const isWild = isWildcardName(name);

  if (isWild && eventType != null) {
    const typeLabel = EVENT_TYPE_TO_LABEL[eventType] ?? eventType;
    return { displayName: `any ${typeLabel}`, isWildcard: true };
  }

  if (isWild) {
    return { displayName: 'any event', isWildcard: true };
  }

  return { displayName: name ?? 'event', isWildcard: false };
}

// =============================================================================
// Condition Parsers
// =============================================================================

/**
 * Parse email validation conditions (type: 'email')
 */
export function parseEmailCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'email') return null;

  const operator = data.operator as string | undefined;
  const inverse = data.inverse === true;

  let operatorLabel: string;
  if (operator === 'email_valid') {
    operatorLabel = inverse ? 'is not a valid email' : 'is a valid email';
  } else if (operator === 'exists') {
    operatorLabel = inverse ? 'does not exist' : 'exists';
  } else {
    operatorLabel = inverse ? 'is not valid or does not exist' : 'is valid and exists';
  }

  return {
    type: 'email',
    name: 'email',
    operator: operatorLabel,
    dataSourceType: 'email',
  };
}

/**
 * Get message type label for display.
 * Detects from condition type (e.g., sent_email) or name pattern (e.g., slack_*, twilio_*).
 */
function getMessageTypeLabel(conditionType: string, messageName?: string): string {
  // Check condition type first
  if (conditionType.endsWith('_email')) return 'email';
  if (conditionType.includes('twilio') || conditionType.includes('sms')) return 'SMS';
  if (conditionType.includes('slack')) return 'Slack message';
  if (conditionType.includes('webhook')) return 'webhook';
  if (conditionType.includes('in_app')) return 'in-app message';
  if (conditionType.includes('push')) return 'push notification';
  if (conditionType.includes('whatsapp')) return 'WhatsApp message';
  if (conditionType.includes('line')) return 'LINE message';
  if (conditionType.includes('urban_airship') || conditionType.includes('airship')) return 'Airship notification';

  // For generic _action types, check the name pattern (e.g., slack_*, twilio_*, webhook_*)
  if (messageName != null) {
    const nameLower = messageName.toLowerCase();
    if (nameLower.startsWith('slack_') || nameLower.startsWith('slack-')) return 'Slack message';
    if (nameLower.startsWith('twilio_') || nameLower.startsWith('twilio-')) return 'SMS';
    if (nameLower.startsWith('webhook_') || nameLower.startsWith('webhook-')) return 'webhook';
    if (nameLower.startsWith('in_app_') || nameLower.startsWith('in-app_') || nameLower.startsWith('in_app-')) {
      return 'in-app message';
    }
    if (nameLower.startsWith('push_') || nameLower.startsWith('push-')) return 'push notification';
    if (nameLower.startsWith('email_') || nameLower.startsWith('email-')) return 'email';
    if (nameLower.startsWith('whatsapp_') || nameLower.startsWith('whatsapp-')) return 'WhatsApp message';
    if (nameLower.startsWith('line_') || nameLower.startsWith('line-')) return 'LINE message';
    if (nameLower.startsWith('urban_airship_') || nameLower.startsWith('airship_')) return 'Airship notification';
  }

  return 'message';
}

/**
 * Format message name for display
 * UI format:
 * - Wildcard (*): "any email"
 * - Campaign (email-campaign_123): "any email in [campaign name]"
 * - Campaign with message name (email-campaign_123_h with enriched message_name): "any email in [message name]"
 * - Specific message: "[message name]" or "any email in [parent name]"
 */
function formatMessageName(
  messageName: string | undefined,
  conditionType: string,
  campaignName?: string,
  messageDisplayName?: string,
): { displayName: string; parentName?: string } {
  const typeLabel = getMessageTypeLabel(conditionType, messageName);

  // Wildcard: any email/sms/etc - patterns like "slack_*", "twilio_*", "*"
  if (isEmptyString(messageName) || (messageName != null && messageName.endsWith('*'))) {
    return { displayName: `any ${typeLabel}` };
  }

  // Campaign format: email-campaign_123 or email-campaign_123_h
  if (messageName.includes('-campaign_')) {
    // Extract campaign ID correctly (handle _h suffix for human opens/clicks)
    // Pattern: "email-campaign_126_h" -> extract "126"
    const campaignIdMatch = messageName.match(CAMPAIGN_ID_PATTERN);
    const campaignId = campaignIdMatch != null ? campaignIdMatch[1] : null;

    // If we have an enriched message name (from enrichConditionWithMessageName),
    // use that instead of the campaign name
    if (!isEmptyString(messageDisplayName)) {
      return {
        displayName: `any ${typeLabel}`,
        parentName: messageDisplayName,
      };
    }

    // Fallback to campaign name or campaign ID
    // If campaign ID extraction failed, use a safe fallback instead of trying to parse the string
    return {
      displayName: `any ${typeLabel}`,
      parentName: campaignName ?? (campaignId != null ? `campaign #${campaignId}` : 'campaign'),
    };
  }

  // Specific message - just show the name
  return { displayName: messageName };
}

/**
 * Get data source type from event type string or name pattern.
 * Returns the appropriate DataSourceType for message conditions.
 */
function getDataSourceTypeFromEvent(eventType: string, eventName?: string): DataSourceType {
  // Check event type first
  if (eventType.includes('email')) return 'email';
  if (eventType.includes('twilio') || eventType.includes('sms')) return 'sms';
  if (eventType.includes('push')) return 'push';
  if (eventType.includes('in_app')) return 'in_app';
  if (eventType.includes('slack')) return 'slack';
  if (eventType.includes('webhook')) return 'webhook';

  // For generic _action types, detect from name pattern
  if (eventName != null) {
    const nameLower = eventName.toLowerCase();
    if (nameLower.startsWith('slack_') || nameLower.startsWith('slack-')) return 'slack';
    if (nameLower.startsWith('twilio_') || nameLower.startsWith('twilio-')) return 'sms';
    if (nameLower.startsWith('webhook_') || nameLower.startsWith('webhook-')) return 'webhook';
    if (nameLower.startsWith('in_app_') || nameLower.startsWith('in-app_') || nameLower.startsWith('in_app-')) {
      return 'in_app';
    }
    if (nameLower.startsWith('push_') || nameLower.startsWith('push-')) return 'push';
    if (nameLower.startsWith('email_') || nameLower.startsWith('email-')) return 'email';
  }

  return 'message';
}

/**
 * Build a message condition result (used for email, sms, push, slack, webhook, etc.)
 * This consolidates the common logic for all message-type conditions.
 */
function buildMessageCondition(params: {
  eventType: string;
  eventName?: string;
  campaignName?: string;
  messageDisplayName?: string;
  inverse: boolean;
  times?: number;
  within?: number;
  filters?: Array<Record<string, unknown>>;
  filtersOperator?: 'and' | 'or';
}): ParsedCondition {
  const { eventType, eventName, campaignName, messageDisplayName, inverse, times, within, filters, filtersOperator } =
    params;

  const { displayName, parentName } = formatMessageName(eventName, eventType, campaignName, messageDisplayName);
  const operator = getMessageEventPrefix(eventType, inverse);
  const dataSourceType = getDataSourceTypeFromEvent(eventType, eventName);

  // Check if this is a human activity event (ends with _h)
  // "by a human" should come after the value, not in the operator
  const isHuman = eventName != null && eventName.endsWith('_h');
  const humanSuffix = isHuman ? ' by a human' : '';

  // Build suffix: combine times/within with "by a human" if both exist
  const eventSuffix = buildEventSuffix(times, within);
  const combinedSuffix =
    eventSuffix !== '' ? `${eventSuffix}${humanSuffix}` : humanSuffix !== '' ? humanSuffix : undefined;

  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  return {
    type: 'message',
    name: displayName,
    value: parentName != null ? `in ${parentName}` : undefined,
    operator,
    suffix: combinedSuffix,
    dataSourceType,
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Check if an event type is a message-related type (email, sms, push, etc.)
 */
function isMessageEventType(eventType: string): boolean {
  return (
    eventType.includes('email') ||
    eventType.includes('twilio') ||
    eventType.includes('sms') ||
    eventType.includes('push') ||
    eventType.includes('in_app') ||
    eventType.includes('slack') ||
    eventType.includes('webhook') ||
    eventType.includes('whatsapp') ||
    eventType.includes('line') ||
    eventType.includes('urban_airship') ||
    eventType.includes('airship') ||
    eventType.includes('action')
  );
}

/**
 * Build common event suffix (times + within).
 * Used by screenview, pageview, form, and event conditions.
 */
function buildEventSuffix(times?: number, within?: number): string {
  let suffix = '';
  if (times != null && times > 1) {
    suffix += ` at least ${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }
  return suffix;
}

/**
 * Build a screenview condition result.
 */
function buildScreenviewCondition(params: {
  eventName?: string;
  inverse: boolean;
  times?: number;
  within?: number;
  filters?: Array<Record<string, unknown>>;
  filtersOperator?: 'and' | 'or';
}): ParsedCondition {
  const { eventName, inverse, times, within, filters, filtersOperator } = params;
  const isWild = isWildcardName(eventName);
  const suffix = buildEventSuffix(times, within);
  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  return {
    type: 'screenview',
    prefix: inverse ? 'Has not' : 'Has',
    operator: isWild ? 'viewed a screen' : 'viewed a screen matching',
    value: isWild ? undefined : eventName,
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'screenview',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Build a pageview condition result.
 */
function buildPageviewCondition(params: {
  eventName?: string;
  inverse: boolean;
  times?: number;
  within?: number;
  filters?: Array<Record<string, unknown>>;
  filtersOperator?: 'and' | 'or';
}): ParsedCondition {
  const { eventName, inverse, times, within, filters, filtersOperator } = params;
  const isWild = isWildcardName(eventName);
  const suffix = buildEventSuffix(times, within);
  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  return {
    type: 'pageview',
    prefix: inverse ? 'Has not' : 'Has',
    operator: isWild ? 'viewed a page' : 'viewed a page with the URL',
    value: isWild ? undefined : eventName,
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'pageview',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Build a form condition result.
 * If formName is wildcard (*) and there's a form_id filter, extract and use the form_id as the name.
 * Uses resolvedFormName (from segment's linked_form_id mapping) if available.
 * Always removes form_id filter from display since it's redundant with the form name.
 */
function buildFormCondition(params: {
  formName?: string;
  resolvedFormName?: string;
  inverse: boolean;
  times?: number;
  within?: number;
  filters?: Array<Record<string, unknown>>;
  filtersOperator?: 'and' | 'or';
}): ParsedCondition {
  const { formName, resolvedFormName, inverse, times, within, filters, filtersOperator } = params;
  const suffix = buildEventSuffix(times, within);

  // Use resolvedFormName (from segment metadata) if available
  let finalFormName = resolvedFormName ?? formName;

  // Always remove form_id filter from displayed filters (it's redundant with form name)
  let remainingFilters = filters;
  if (filters != null && filters.length > 0) {
    // Look for form_id filter
    const formIdFilter = filters.find((f) => {
      const field = (f.field ?? f.attribute) as string | undefined;
      return field === 'form_id';
    });

    if (formIdFilter != null) {
      // Use form_id value as the form name if we don't have one
      if (finalFormName == null || finalFormName === '' || finalFormName === '*' || finalFormName === 'form_submit') {
        finalFormName = formIdFilter.value as string | undefined;
      }
      // Remove form_id filter from displayed filters
      remainingFilters = filters.filter((f) => {
        const field = (f.field ?? f.attribute) as string | undefined;
        return field !== 'form_id';
      });
    }
  }

  const parsedFilters =
    remainingFilters != null && remainingFilters.length > 0 ? parseFilters(remainingFilters) : undefined;

  return {
    type: 'form',
    name: finalFormName ?? 'form',
    operator: inverse ? 'not submitted' : 'submitted',
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'form',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Parse message action conditions (types ending in _email or _action)
 * UI format: "[any email] in [campaign name] has never been sent"
 */
export function parseMessageActionCondition(data: Record<string, unknown>): ParsedCondition | null {
  const conditionType = data.type;
  // conditionType must be a string ending in _email or _action (not a number like 0, 1, 2)
  if (
    conditionType == null ||
    typeof conditionType !== 'string' ||
    (!conditionType.endsWith('_email') && !conditionType.endsWith('_action'))
  ) {
    return null;
  }

  const inverse = data.inverse === true;
  const messageName = data.name as string | undefined;
  const campaignName = data.campaign_name as string | undefined; // From enrichment
  const href = data.href as string | undefined;
  const times = data.times as number | undefined;
  const within = data.within as number | undefined;

  // Format message name with campaign support
  // Check for enriched message name (from enrichConditionWithMessageName)
  const messageDisplayName = data.message_name as string | undefined;
  const { displayName, parentName } = formatMessageName(messageName, conditionType, campaignName, messageDisplayName);

  // Check if this is a human activity event (message name ends with _h)
  // "by a human" should come after the value, not in the operator
  const isHuman = messageName != null && messageName.endsWith('_h');
  const humanSuffix = isHuman ? ' by a human' : '';

  // Map action type to operator label (without "by a human" suffix)
  let operatorLabel: string;
  switch (conditionType) {
    case 'sent_email':
    case 'sent_action':
      operatorLabel = inverse ? 'has never been sent' : 'has ever been sent';
      break;
    case 'delivered_email':
    case 'delivered_action':
      operatorLabel = inverse ? 'has never been delivered' : 'has been delivered';
      break;
    case 'opened_email':
    case 'opened_action':
      operatorLabel = inverse ? 'has never been opened' : 'has been opened';
      break;
    case 'clicked_email':
    case 'clicked_action':
      operatorLabel = inverse ? 'has never been clicked' : 'has been clicked';
      break;
    case 'converted_email':
    case 'converted_action':
      operatorLabel = inverse ? 'has never converted' : 'has converted';
      break;
    case 'bounced_email':
    case 'bounced_action':
      operatorLabel = inverse ? 'has never bounced' : 'has bounced';
      break;
    case 'failed_email':
    case 'failed_action':
      operatorLabel = inverse ? 'has never failed to receive' : 'has failed to receive';
      break;
    case 'dropped_email':
    case 'dropped_action':
      operatorLabel = inverse ? 'has never been suppressed' : 'has been suppressed';
      break;
    case 'spammed_email':
      operatorLabel = inverse ? 'has never been spammed' : 'has been spammed';
      break;
    case 'unsubscribed_email':
      operatorLabel = inverse ? 'has never unsubscribed' : 'has unsubscribed';
      break;
    case 'replied_action':
      operatorLabel = inverse ? 'has never replied' : 'has replied';
      break;
    case 'undeliverable_action':
      operatorLabel = inverse ? 'has never been undeliverable' : 'has been undeliverable';
      break;
    default:
      operatorLabel = inverse ? 'has not interacted with' : 'has interacted with';
  }

  // Build suffix with optional click details, times, within, and "by a human"
  let suffix = '';
  if (href != null) {
    suffix += `on the tracked link matching "${href}"`;
  }
  if (times != null && times > 1) {
    suffix += suffix !== '' ? ' ' : '';
    suffix += 'at least ';
    suffix += `${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }
  // Add "by a human" at the end if this is a human activity event
  if (humanSuffix !== '') {
    suffix += humanSuffix;
  }

  // Determine data source type based on channel
  let dataSourceType: DataSourceType = 'message';
  if (conditionType.endsWith('_email')) {
    dataSourceType = 'email';
  } else if (messageName != null) {
    // Detect channel from message name pattern
    if (messageName.includes('twilio') || messageName.includes('sms')) {
      dataSourceType = 'sms';
    } else if (messageName.includes('slack')) {
      dataSourceType = 'slack';
    } else if (messageName.includes('webhook')) {
      dataSourceType = 'webhook';
    } else if (messageName.includes('in_app') || messageName.includes('in-app')) {
      dataSourceType = 'in_app';
    } else if (messageName.includes('push')) {
      dataSourceType = 'push';
    }
  }

  return {
    type: 'message_action',
    name: displayName,
    value: parentName != null ? `in ${parentName}` : undefined,
    operator: operatorLabel,
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType,
  };
}

/**
 * Parse pageview conditions
 */
export function parsePageviewCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Check for type: 'pageview' or event with pageview type
  if (data.type !== 'pageview') {
    const eventData = data.event as Record<string, unknown> | undefined;
    const eventType = (eventData?.type ?? eventData?.ui_type) as string | undefined;
    if (eventType !== 'pageview') return null;
  }

  const inverse = data.inverse === true;
  const url = (data.url ?? data.name ?? (data.event as Record<string, unknown> | undefined)?.url) as string | undefined;
  const times = data.times as number | undefined;
  const within = data.within as number | undefined;
  const filtersData = extractFiltersArray(data.filters);
  const filters = filtersData?.filters;
  const filtersOperator =
    filtersData?.operator ?? ((data.filters_operator ?? data.filtersOperator) as 'and' | 'or' | undefined);

  // UI format: "Has/Has not viewed a page" or "Has/Has not viewed a page with the URL [url]"
  const isWildcard = isWildcardName(url);

  // Build suffix with times, within, filters
  let suffix = '';
  if (times != null && times > 1) {
    suffix += ' at least ';
    suffix += `${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }

  // Parse filters for separate display
  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  // For activity conditions, use: prefix (bold) → operator (not bold) → value (bold)
  // When wildcard, no URL is shown at all
  return {
    type: 'pageview',
    prefix: inverse ? 'Has not' : 'Has',
    operator: isWildcard ? 'viewed a page' : 'viewed a page with the URL',
    value: isWildcard ? undefined : url,
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'pageview',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Parse screenview conditions
 */
export function parseScreenviewCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'screenview') {
    const eventData = data.event as Record<string, unknown> | undefined;
    const eventType = (eventData?.type ?? eventData?.ui_type) as string | undefined;
    if (eventType !== 'screenview') return null;
  }

  const inverse = data.inverse === true;
  const screen = (data.screen ?? data.name ?? (data.event as Record<string, unknown> | undefined)?.screen) as
    | string
    | undefined;
  const times = data.times as number | undefined;
  const within = data.within as number | undefined;

  // UI format: "Has/Has not viewed a screen" or "Has/Has not viewed a screen matching [name]"
  const isWildcard = isWildcardName(screen);

  // Build suffix with times, within
  let suffix = '';
  if (times != null && times > 1) {
    suffix += ' at least ';
    suffix += `${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }

  // For activity conditions, use: prefix (bold) → operator (not bold) → value (bold)
  // When wildcard, no name is shown at all
  return {
    type: 'screenview',
    prefix: inverse ? 'Has not' : 'Has',
    operator: isWildcard ? 'viewed a screen' : 'viewed a screen matching',
    value: isWildcard ? undefined : screen,
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'screenview',
  };
}

/**
 * Parse form submission conditions
 */
export function parseFormCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'form') {
    const eventData = data.event as Record<string, unknown> | undefined;
    const eventType = (eventData?.type ?? eventData?.ui_type) as string | undefined;
    if (eventType !== 'form_submit' && eventType !== 'form') return null;
  }

  const inverse = data.inverse === true;
  const formName = (data.name ?? (data.event as Record<string, unknown> | undefined)?.form_id ?? data.form_id) as
    | string
    | undefined;
  const times = data.times as number | undefined;
  const within = data.within as number | undefined;
  const filtersData = extractFiltersArray(data.filters);
  const filters = filtersData?.filters;
  const filtersOperator =
    filtersData?.operator ?? ((data.filters_operator ?? data.filtersOperator) as 'and' | 'or' | undefined);

  // UI format: "[formName] submitted/not submitted"
  // Build suffix with times, within only
  let suffix = '';
  if (times != null && times > 1) {
    suffix += ' at least ';
    suffix += `${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }

  // Parse filters for separate display
  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  // Form format: [name] [operator] (name bold, operator not bold)
  return {
    type: 'form',
    name: formName ?? 'form',
    operator: inverse ? 'not submitted' : 'submitted',
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'form',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Parse inbound message conditions
 */
export function parseInboundCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'inbound') {
    const eventData = data.event as Record<string, unknown> | undefined;
    const eventType = (eventData?.type ?? eventData?.ui_type) as string | undefined;
    if (eventType !== 'inbound') return null;
  }

  const inverse = data.inverse === true;
  const times = data.times as number | undefined;
  const within = data.within as number | undefined;
  const filtersData = extractFiltersArray(data.filters);
  const filters = filtersData?.filters;
  const filtersOperator =
    filtersData?.operator ?? ((data.filters_operator ?? data.filtersOperator) as 'and' | 'or' | undefined);

  // UI format: "Inbound SMS [received/not received] [times] [within] [filters]"
  // Build suffix with times, within, filters
  let suffix = '';
  if (times != null && times > 1) {
    suffix += ' at least ';
    suffix += `${times} ${pluralize(times, 'time', 'times')}`;
  }
  if (within != null && within > 0) {
    suffix += ` within ${formatWithin(within)}`;
  }

  // Parse filters for separate display
  const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

  return {
    type: 'inbound',
    name: 'Inbound SMS',
    operator: inverse ? 'not received' : 'received',
    suffix: suffix !== '' ? suffix : undefined,
    dataSourceType: 'inbound',
    filters: parsedFilters,
    filtersOperator: parsedFilters != null ? filtersOperator : undefined,
  };
}

/**
 * Parse opt-out conditions
 */
export function parseOptOutCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'opt_out' && !('optout' in data)) return null;

  const inverse = data.inverse === true;
  const number = (data.number ?? data.channel) as string | undefined;

  // Handle optout object format
  if ('optout' in data) {
    const optout = data.optout as Record<string, unknown>;
    const subscribed = optout.subscribed === true;
    return {
      type: 'opt_out',
      prefix: subscribed ? 'is subscribed to' : 'is unsubscribed from',
      name: 'communications',
      dataSourceType: 'opt_out',
    };
  }

  // UI format: "User is opted out of / User is not opted out of [number]"
  return {
    type: 'opt_out',
    prefix: inverse ? 'User is not opted out of' : 'User is opted out of',
    name: number ?? 'SMS',
    dataSourceType: 'opt_out',
  };
}

/**
 * Parse relationship conditions.
 * Handles multiple formats:
 * 1. { type: 'relationship', inverse, objectName }
 * 2. { type: 3, relationship_filter: { type_id, inverse, and } } - backend condition format
 * 3. { relationship: { type_id, inverse } } - backend serialized format
 * 4. { and: [{ relationship: { type_id, inverse } }, ...filters] } - with filters
 *
 * UI format: "Relationship to [object name] exists/does not exist"
 * With filters: "with [all/at least one] of the following conditions: [filters]"
 *
 * See: ~/code/ui/app/components/cio-condition-viewer-relationship/index.gts
 */
export function parseRelationshipCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Handle type: 3 (CONDITION_TYPE.RELATIONSHIP) with relationship_filter
  if (data.type === CONDITION_TYPE.RELATIONSHIP || data.type === 3) {
    const relFilter = data.relationship_filter as Record<string, unknown> | undefined;
    if (relFilter != null) {
      const inverse = relFilter.inverse === true;
      const typeId = relFilter.type_id as number | string | undefined;
      // objectName may come from enrichment
      const objectName = data.object_type_name as string | undefined;

      // Extract filters if present (can be in refinement or filters property)
      const filtersData = extractFiltersArrayForRelationship(relFilter.refinement ?? relFilter.filters);
      const filters = filtersData?.filters;
      // and: true means "matches all", and: false means "matches any"
      const filtersOperator = filtersData?.operator ?? (relFilter.and === true ? 'and' : 'or');
      const parsedFilters = filters != null && filters.length > 0 ? parseRelationshipFilters(filters) : undefined;

      return {
        type: 'relationship',
        prefix: 'Relationship to',
        name: objectName ?? (typeId != null ? `object #${typeId}` : 'object'),
        operator: inverse ? 'does not exist' : 'exists',
        dataSourceType: 'relationship',
        filters: parsedFilters,
        filtersOperator: parsedFilters != null ? filtersOperator : undefined,
      };
    }
  }

  // Handle type: 'relationship' or type: 'related' (direct format)
  if (data.type === 'relationship' || data.type === 'related') {
    const inverse = data.inverse === true;
    // objectName may come from enrichment (object_type_name) or direct property
    const objectName = (data.object_type_name ?? data.objectName ?? data.object_name) as string | undefined;
    const objectTypeId = (data.object_type_id ?? data.type_id ?? data.typeId) as number | string | undefined;

    // Get filters if present
    const filtersData = extractFiltersArrayForRelationship(data.refinement ?? data.filters);
    const filters = filtersData?.filters;
    const filtersOperator = filtersData?.operator;
    const parsedFilters = filters != null && filters.length > 0 ? parseRelationshipFilters(filters) : undefined;

    return {
      type: 'relationship',
      prefix: 'Relationship to',
      name: objectName ?? (objectTypeId != null ? `object #${objectTypeId}` : 'object'),
      operator: inverse ? 'does not exist' : 'exists',
      dataSourceType: 'relationship',
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  // Handle { relationship: { type_id, inverse } } format (backend serialized)
  if ('relationship' in data && typeof data.relationship === 'object' && data.relationship != null) {
    const rel = data.relationship as Record<string, unknown>;
    const inverse = rel.inverse === true;
    const typeId = rel.type_id as number | string | undefined;
    // objectName may come from enrichment
    const objectName = (data.object_type_name ?? rel.object_name) as string | undefined;

    // Extract filters if present (can be in refinement or filters property)
    const filtersData = extractFiltersArrayForRelationship(rel.refinement ?? rel.filters);
    const filters = filtersData?.filters;
    const filtersOperator = filtersData?.operator ?? (rel.and === true ? 'and' : 'or');
    const parsedFilters = filters != null && filters.length > 0 ? parseRelationshipFilters(filters) : undefined;

    return {
      type: 'relationship',
      prefix: 'Relationship to',
      name: objectName ?? (typeId != null ? `object #${typeId}` : 'object'),
      operator: inverse ? 'does not exist' : 'exists',
      dataSourceType: 'relationship',
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  // Handle { relationship_filter: { type_id, inverse } } without type: 3
  if (
    'relationship_filter' in data &&
    typeof data.relationship_filter === 'object' &&
    data.relationship_filter != null
  ) {
    const relFilter = data.relationship_filter as Record<string, unknown>;
    const inverse = relFilter.inverse === true;
    const typeId = relFilter.type_id as number | string | undefined;
    const objectName = data.object_type_name as string | undefined;

    // Extract filters if present (can be in refinement or filters property)
    const filtersData = extractFiltersArrayForRelationship(relFilter.refinement ?? relFilter.filters);
    const filters = filtersData?.filters;
    const filtersOperator = filtersData?.operator ?? (relFilter.and === true ? 'and' : 'or');
    const parsedFilters = filters != null && filters.length > 0 ? parseRelationshipFilters(filters) : undefined;

    return {
      type: 'relationship',
      prefix: 'Relationship to',
      name: objectName ?? (typeId != null ? `object #${typeId}` : 'object'),
      operator: inverse ? 'does not exist' : 'exists',
      dataSourceType: 'relationship',
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  // Handle { and: [{ relationship: {...} }, ...filters] } format
  if ('and' in data && Array.isArray(data.and) && data.and.length > 0) {
    const andArray = data.and as Array<Record<string, unknown>>;
    const relationshipItem = andArray.find(
      (item) => 'relationship' in item && typeof item.relationship === 'object' && item.relationship != null,
    );

    if (relationshipItem != null) {
      const rel = relationshipItem.relationship as Record<string, unknown>;
      const inverse = rel.inverse === true;
      const typeId = rel.type_id as number | string | undefined;
      const objectName = (data.object_type_name ?? rel.object_name) as string | undefined;

      // Get filters from other items in the and array
      const filterItems = andArray.filter((item) => !('relationship' in item));
      const parsedFilters =
        filterItems.length > 0 ? parseRelationshipFilters(filterItems as Record<string, unknown>[]) : undefined;

      return {
        type: 'relationship',
        prefix: 'Relationship to',
        name: objectName ?? (typeId != null ? `object #${typeId}` : 'object'),
        operator: inverse ? 'does not exist' : 'exists',
        dataSourceType: 'relationship',
        filters: parsedFilters,
        filtersOperator: parsedFilters != null ? 'and' : undefined,
      };
    }
  }

  return null;
}

/**
 * Extract filters array from relationship condition refinement/filters.
 * Handles: { and: [...] }, { or: [...] }, or direct array
 */
function extractFiltersArrayForRelationship(
  filtersData: unknown,
): { filters: Record<string, unknown>[]; operator: 'and' | 'or' } | null {
  if (filtersData == null) return null;

  if (Array.isArray(filtersData)) {
    return { filters: filtersData as Record<string, unknown>[], operator: 'and' };
  }

  if (typeof filtersData === 'object') {
    const obj = filtersData as Record<string, unknown>;

    if ('and' in obj && Array.isArray(obj.and)) {
      return { filters: obj.and as Record<string, unknown>[], operator: 'and' };
    }

    if ('or' in obj && Array.isArray(obj.or)) {
      return { filters: obj.or as Record<string, unknown>[], operator: 'or' };
    }
  }

  return null;
}

/**
 * Parse relationship attribute filters.
 * Handles: { related_object_attribute: {...} }, { relationship_attribute: {...} }, or direct filter
 */
function parseRelationshipFilters(filters: Record<string, unknown>[]): ParsedFilter[] {
  const result: ParsedFilter[] = [];

  for (const filter of filters) {
    // Handle wrapped attribute sources
    const attrData =
      (filter.related_object_attribute as Record<string, unknown>) ??
      (filter.relationship_attribute as Record<string, unknown>) ??
      (filter.trigger_object_attribute as Record<string, unknown>) ??
      (filter.trigger_relationship_attribute as Record<string, unknown>) ??
      (filter.object_attribute as Record<string, unknown>) ??
      filter;

    const field = (attrData.field ?? attrData.attribute ?? attrData.name) as string | undefined;
    const operator = attrData.operator as string | undefined;
    const value = attrData.value;
    const inverse = attrData.inverse === true;

    if (field == null) continue;

    const operatorLabel = (operator != null ? getOperatorLabel(operator, inverse) : undefined) ?? 'is';

    result.push({
      field,
      operator: operatorLabel,
      // Use timestamp-aware formatting for date operators
      value: value != null ? formatValueWithOperator(value, operator) : undefined,
    });
  }

  return result;
}

/**
 * Parse segment reference conditions
 * UI format: "In [segment name] segment" / "Not in [segment name] segment"
 * Fallback: "In segment #123" / "Not in segment #123" (when name not available)
 *
 * Supports multiple formats:
 * 1. { segment_id: number, inverse?: boolean } - Standard segment reference
 * 2. { type: 'segment', segment_id?: number } - Type-based segment
 * 3. { segment: { id, inverse } } - Nested segment object
 * 4. { type: 'segments_negatable', segments: number[], negate?: boolean } - Newsletter recipients format
 */
export function parseSegmentCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Handle segments_negatable format (used by newsletter recipients)
  // Format: { type: 'segments_negatable', segments: [1, 2, 3], negate: boolean }
  if (data.type === 'segments_negatable' && 'segments' in data && Array.isArray(data.segments)) {
    const segments = data.segments as (string | number)[];
    const negate = data.negate === true;

    // If no segments, return null
    if (segments.length === 0) {
      return null;
    }

    // For a single segment, show "In segment #X" format
    if (segments.length === 1) {
      const segmentId = typeof segments[0] === 'string' ? Number.parseInt(segments[0], 10) : segments[0];
      // Check if segment name is available (from enriched data like segment_names map)
      const segmentNames = data.segment_names as Record<string, string> | undefined;
      const segmentName = segmentNames != null ? segmentNames[String(segmentId)] : undefined;

      return {
        type: 'segment',
        prefix: negate ? 'Not in' : 'In',
        name: segmentName ?? `segment #${segmentId}`,
        suffix: segmentName != null ? ' segment' : undefined,
        dataSourceType: 'segment',
        segmentId: segmentId,
        inverse: negate,
      };
    }

    // For multiple segments, show "In segments #X, #Y, #Z" format
    const segmentNames = data.segment_names as Record<string, string> | undefined;
    const segmentLabels = segments.map((seg) => {
      const id = typeof seg === 'string' ? Number.parseInt(seg, 10) : seg;
      const name = segmentNames != null ? segmentNames[String(id)] : undefined;
      return name != null ? `${name} (#${id})` : `#${id}`;
    });

    return {
      type: 'segment',
      prefix: negate ? 'Not in' : 'In',
      name: segmentLabels.length > 1 ? `segments ${segmentLabels.join(', ')}` : `segment ${segmentLabels[0]}`,
      dataSourceType: 'segment',
      inverse: negate,
    };
  }

  // Handle segment_id reference
  if ('segment_id' in data && typeof data.segment_id === 'number' && data.segment_id > 0) {
    const inverse = data.inverse === true;
    // Check if segment name is available (from enriched data)
    const segmentName = data.segment_name as string | undefined;
    // If we have a name, use "name segment". If not, use "segment #123" (no duplicate "segment")
    return {
      type: 'segment',
      prefix: inverse ? 'Not in' : 'In',
      name: segmentName ?? `segment #${data.segment_id}`,
      suffix: segmentName != null ? ' segment' : undefined,
      dataSourceType: 'segment',
      segmentId: data.segment_id,
      inverse,
    };
  }

  // Handle type: 'segment'
  if (data.type === 'segment') {
    const segmentId = data.segment_id as number | undefined;
    const segmentName = data.segment_name as string | undefined;
    const inverse = data.inverse === true;
    return {
      type: 'segment',
      prefix: inverse ? 'Not in' : 'In',
      name: segmentName ?? (segmentId != null ? `segment #${segmentId}` : 'segment'),
      suffix: segmentName != null ? ' segment' : undefined,
      dataSourceType: 'segment',
      segmentId: segmentId ?? undefined,
      inverse,
    };
  }

  // Handle { segment: { id, inverse } } format
  if ('segment' in data && typeof data.segment === 'object' && data.segment != null) {
    const seg = data.segment as Record<string, unknown>;
    const segmentId = seg.id as number | undefined;
    // Check for segment name from enriched data (segment_name or segment_names map)
    const segmentName =
      ((seg.name ?? data.segment_name) as string | undefined) ??
      (segmentId != null && data.segment_names != null
        ? (data.segment_names as Record<string, string>)[String(segmentId)]
        : undefined);
    const inverse = seg.inverse === true;
    return {
      type: 'segment',
      prefix: inverse ? 'Not in' : 'In',
      name: segmentName ?? (segmentId != null ? `segment #${segmentId}` : 'segment'),
      suffix: segmentName != null ? ' segment' : undefined,
      dataSourceType: 'segment',
      segmentId: segmentId ?? undefined,
      inverse,
    };
  }

  return null;
}

/**
 * Parse event conditions (performed event)
 */
export function parseEventCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Check for event_filter structure (from condition audit logs)
  if ('event_filter' in data && data.event_filter != null) {
    const eventFilter = data.event_filter as Record<string, unknown>;
    const eventTypeId = data.event_type_id as number | undefined;

    // Get event info from enriched data (event object added by enrichConditionWithEventType)
    const eventObj = data.event as Record<string, unknown> | undefined;
    const rawEventName =
      (eventObj?.name as string | undefined) ??
      (data.event_name as string | undefined) ??
      (eventFilter.name as string | undefined);
    // Use ui_type for categorization (more specific), fall back to type
    const eventType = (eventObj?.type as string | undefined) ?? (eventObj?.ui_type as string | undefined);
    const uiType = eventObj?.ui_type as string | undefined;

    // Format the event name (handles wildcards like "*" → "any email")
    const { displayName: eventName } = formatEventName(rawEventName, eventType);

    // Fallback to event type ID if no name available
    const finalName = rawEventName != null ? eventName : eventTypeId != null ? `Event #${eventTypeId}` : 'event';

    const eventTimes = eventFilter.times as number | undefined;
    const eventWithin = eventFilter.within as number | undefined;
    const times = eventTimes ?? 1;
    const inverse = (eventFilter.inverse as boolean) === true;

    // Get filters (nested conditions on the event)
    // Filters can be in eventFilter.filters OR in eventObj.filters (from EventType enrichment)
    // Filters can be: array, { and: [...] }, { or: [...] }, or single object
    const filtersData = extractFiltersArray(eventObj?.filters ?? eventFilter.filters);
    const filters = filtersData?.filters;
    const filtersOperator =
      filtersData?.operator ??
      ((eventFilter.filters_operator ?? eventFilter.filtersOperator) as 'and' | 'or' | undefined);

    // Determine the operator based on the event type
    // UI format: [name] [operator] [times] [within] [filters]
    // Example: "purchase performed at least 3 times within 7 days"
    let operator: string;
    const dataSourceType: DataSourceType = 'event';

    // Handle different event type categories
    if (eventType != null) {
      if (eventType === 'screen' || eventType === 'screenview') {
        return buildScreenviewCondition({
          eventName: rawEventName,
          inverse,
          times,
          within: eventWithin,
          filters,
          filtersOperator,
        });
      } else if (eventType === 'page' || eventType === 'pageview') {
        return buildPageviewCondition({
          eventName: rawEventName,
          inverse,
          times,
          within: eventWithin,
          filters,
          filtersOperator,
        });
      } else if (eventType === 'form_submit' || eventType === 'form' || uiType === 'form') {
        // Form submission event - use ui_type or eventType to detect
        return buildFormCondition({
          formName: rawEventName,
          resolvedFormName: data.form_name as string | undefined,
          inverse,
          times,
          within: eventWithin,
          filters,
          filtersOperator,
        });
      } else if (isMessageEventType(eventType)) {
        // All message types: email, sms, push, in_app, slack, webhook, whatsapp, line, airship, action
        // Pass rawEventName to buildMessageCondition so it can detect _h suffix for "by a human"
        return buildMessageCondition({
          eventType,
          eventName: rawEventName,
          campaignName: eventObj?.campaign_name as string | undefined,
          messageDisplayName: eventObj?.message_name as string | undefined,
          inverse,
          times,
          within: eventWithin,
          filters,
          filtersOperator,
        });
      } else {
        operator = inverse ? 'not performed' : 'performed';
      }
    } else {
      operator = inverse ? 'not performed' : 'performed';
    }

    // Build suffix with times, within, and filters
    let suffix = '';

    // Add times
    if (times != null && times > 1) {
      suffix += ' at least ';
      suffix += `${times} ${pluralize(times, 'time', 'times')}`;
    }

    // Add within
    if (eventWithin != null && eventWithin > 0) {
      suffix += ` within ${formatWithin(eventWithin)}`;
    }

    // Parse filters for separate display (not in suffix)
    const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

    // Event format: [name] [operator] (name first, then operator)
    return {
      type: 'event',
      name: finalName,
      operator,
      suffix: suffix !== '' ? suffix : undefined,
      dataSourceType,
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  // Check for type: 'event'
  if (data.type === 'event') {
    const eventName = data.name as string | undefined;
    const inverse = data.inverse === true;
    const times = data.times as number | undefined;
    const within = data.within as number | undefined;
    const filtersData = extractFiltersArray(data.filters);
    const filters = filtersData?.filters;
    const filtersOperator =
      filtersData?.operator ?? ((data.filters_operator ?? data.filtersOperator) as 'and' | 'or' | undefined);

    // Build suffix (times and within only, filters handled separately)
    let suffix = '';
    if (times != null && times > 1) {
      suffix += ' at least ';
      suffix += `${times} ${pluralize(times, 'time', 'times')}`;
    }
    if (within != null && within > 0) {
      suffix += ` within ${formatWithin(within)}`;
    }

    // Parse filters for separate display
    const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

    // Event format: [name] [operator] (name first, then operator)
    return {
      type: 'event',
      name: eventName ?? 'event',
      operator: inverse ? 'not performed' : 'performed',
      suffix: suffix !== '' ? suffix : undefined,
      dataSourceType: 'event',
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  // Check for { event: { name, ... } } format
  if ('event' in data && typeof data.event === 'object' && data.event != null) {
    const event = data.event as Record<string, unknown>;
    const eventType = (event.type ?? event.ui_type) as string | undefined;

    // Skip if this is a pageview/screenview/form/inbound/device event (handled by specific parsers)
    if (
      eventType === 'pageview' ||
      eventType === 'screenview' ||
      eventType === 'form_submit' ||
      eventType === 'form' ||
      eventType === 'device_change'
    ) {
      return null;
    }

    const rawEventName = event.name as string | undefined;
    const { displayName: eventName } = formatEventName(rawEventName, eventType);
    const inverse = event.inverse === true || data.inverse === true;
    const times = (event.times ?? data.times) as number | undefined;
    const within = (event.within ?? data.within) as number | undefined;
    const filtersData = extractFiltersArray(event.filters ?? data.filters);
    const filters = filtersData?.filters;
    const filtersOperator =
      filtersData?.operator ??
      ((event.filters_operator ?? event.filtersOperator ?? data.filters_operator ?? data.filtersOperator) as
        | 'and'
        | 'or'
        | undefined);

    // Build suffix (times and within only, filters handled separately)
    let suffix = '';
    if (times != null && times > 1) {
      suffix += ' at least ';
      suffix += `${times} ${pluralize(times, 'time', 'times')}`;
    }
    if (within != null && within > 0) {
      suffix += ` within ${formatWithin(within)}`;
    }

    // Parse filters for separate display
    const parsedFilters = filters != null && filters.length > 0 ? parseFilters(filters) : undefined;

    // Event format: [name] [operator] (name first, then operator)
    return {
      type: 'event',
      name: eventName,
      operator: inverse ? 'not performed' : 'performed',
      suffix: suffix !== '' ? suffix : undefined,
      dataSourceType: 'event',
      filters: parsedFilters,
      filtersOperator: parsedFilters != null ? filtersOperator : undefined,
    };
  }

  return null;
}

/**
 * Get prefix for message events (email, SMS, push, etc.)
 * @param eventType - The event type (e.g., "opened_email", "clicked_email")
 * @param inverse - Whether the condition is negated
 */
function getMessageEventPrefix(eventType: string, inverse: boolean): string {
  if (eventType.startsWith('sent_')) {
    return inverse ? 'has never been sent' : 'has been sent';
  }
  if (eventType.startsWith('delivered_')) {
    return inverse ? 'has never been delivered' : 'has been delivered';
  }
  if (eventType.startsWith('opened_')) {
    return inverse ? 'has never been opened' : 'has been opened';
  }
  if (eventType.startsWith('clicked_')) {
    return inverse ? 'has never been clicked' : 'has been clicked';
  }
  if (eventType.startsWith('bounced_')) {
    return inverse ? 'has never bounced' : 'has bounced';
  }
  if (eventType.startsWith('converted_')) {
    return inverse ? 'has never converted' : 'has converted';
  }
  if (eventType.startsWith('failed_')) {
    return inverse ? 'has never failed to receive' : 'has failed to receive';
  }
  if (eventType.startsWith('dropped_')) {
    return inverse ? 'has never been suppressed' : 'has been suppressed';
  }
  if (eventType.startsWith('unsubscribed_')) {
    return inverse ? 'has never unsubscribed' : 'has unsubscribed';
  }
  if (eventType.startsWith('spammed_')) {
    return inverse ? 'has never been spammed' : 'has been spammed';
  }
  // Default
  return inverse ? 'has not received' : 'has received';
}

/**
 * Parse device attribute conditions
 */
export function parseDeviceAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Check for direct type or type: 2 (DEVICE)
  const conditionType = data.type;

  // Also check for { event: { type: 'device_change', name: 'id', ... } } structure
  const eventObj = data.event as Record<string, unknown> | undefined;
  const eventType = eventObj?.type as string | undefined;

  // Accept: 'device_attribute', 'device_change', CONDITION_TYPE.DEVICE (2), or event.type === 'device_change'
  const isDeviceCondition =
    conditionType === 'device_attribute' ||
    conditionType === 'device_change' ||
    conditionType === CONDITION_TYPE.DEVICE ||
    eventType === 'device_change';

  if (!isDeviceCondition) {
    return null;
  }

  // Extract field from various possible locations
  let field: string | undefined;
  let operator: string | undefined;
  let value: unknown;
  let value2: unknown;
  let inverse: boolean;

  if (eventType === 'device_change' && eventObj != null) {
    // Device condition via { event: { type: 'device_change', name: 'id', filters: {...} } }
    field = eventObj.name as string | undefined;

    // Get operator and value from filters
    // Device conditions store operator/value in filters.and[0] (the 'to' filter)
    const filters = eventObj.filters as Record<string, unknown> | undefined;
    const andFilters = filters?.and as Record<string, unknown>[] | undefined;
    if (andFilters != null && andFilters.length > 0) {
      const toFilter = andFilters[0];
      operator = toFilter.operator as string | undefined;
      value = toFilter.value;
      value2 = toFilter.value2;
      inverse = toFilter.inverse === true;
    } else {
      operator = undefined;
      value = undefined;
      value2 = undefined;
      inverse = data.inverse === true;
    }
  } else if ('attribute_filter' in data && data.attribute_filter != null) {
    // Device condition via attribute_filter (type: 2 backend format)
    const attrFilter = data.attribute_filter as Record<string, unknown>;
    field = (attrFilter.attribute ?? attrFilter.field) as string | undefined;
    operator = attrFilter.operator as string | undefined;
    value = attrFilter.value;
    value2 = attrFilter.value2;
    inverse = attrFilter.inverse === true;
  } else {
    // Direct device attribute format
    field = (data.attribute ?? data.field ?? data.name) as string | undefined;
    operator = data.operator as string | undefined;
    value = data.value;
    value2 = data.value2;
    inverse = data.inverse === true;
  }

  if (field == null) return null;

  // Handle special device field mappings (matching UI behavior)
  // See: ~/code/ui/app/components/condition-editor-v2/nodes/device-change/view.gts
  // Normalize operator: inverse + exists = not_exists
  const effectiveOperator = inverse && operator === 'exists' ? 'not_exists' : operator;

  switch (field) {
    case 'id': {
      // "id exists" → "has a device"
      // "id not_exists" (or inverse + exists) → "does not have any devices"
      if (effectiveOperator === 'exists') {
        return {
          type: 'device_attribute',
          operator: 'has',
          value: 'a device',
          dataSourceType: 'device',
        };
      }
      return {
        type: 'device_attribute',
        operator: 'does not have',
        value: 'any devices',
        dataSourceType: 'device',
      };
    }

    case '_last_status': {
      // "_last_status exists" → "Last status is not unused"
      // "_last_status not_exists" (or inverse + exists) → "Last status is unused"
      if (effectiveOperator === 'exists') {
        return {
          type: 'device_attribute',
          name: 'Last status',
          operator: 'is not',
          value: 'unused',
          dataSourceType: 'device',
        };
      }
      if (effectiveOperator === 'not_exists') {
        return {
          type: 'device_attribute',
          name: 'Last status',
          operator: 'is',
          value: 'unused',
          dataSourceType: 'device',
        };
      }
      // Other operators for _last_status
      const lastStatusOperator = operator != null ? getOperatorLabel(operator, inverse) : undefined;
      return {
        type: 'device_attribute',
        name: 'Last status',
        operator: lastStatusOperator,
        value: value != null ? formatValueWithOperator(value, operator) : undefined,
        value2: value2 != null ? formatValueWithOperator(value2, operator) : undefined,
        dataSourceType: 'device',
      };
    }

    case '_last_used': {
      // "_last_used not_exists" (or inverse + exists) → "Last used was never"
      if (effectiveOperator === 'not_exists') {
        return {
          type: 'device_attribute',
          name: 'Last used',
          operator: 'was',
          value: 'never',
          dataSourceType: 'device',
        };
      }
      // Other operators for _last_used
      const lastUsedOperator = operator != null ? getOperatorLabel(operator, inverse) : undefined;
      return {
        type: 'device_attribute',
        name: 'Last used',
        operator: lastUsedOperator,
        value: value != null ? formatValueWithOperator(value, operator) : undefined,
        value2: value2 != null ? formatValueWithOperator(value2, operator) : undefined,
        dataSourceType: 'device',
      };
    }

    case 'device_model': {
      const modelOperator = operator != null ? getOperatorLabel(operator, inverse) : undefined;
      return {
        type: 'device_attribute',
        name: 'Device model',
        operator: modelOperator,
        value: value != null ? formatValueWithOperator(value, operator) : undefined,
        value2: value2 != null ? formatValueWithOperator(value2, operator) : undefined,
        dataSourceType: 'device',
      };
    }

    case 'platform': {
      const platformOperator = operator != null ? getOperatorLabel(operator, inverse) : undefined;
      return {
        type: 'device_attribute',
        name: 'Platform',
        operator: platformOperator,
        value: value != null ? formatValueWithOperator(value, operator) : undefined,
        value2: value2 != null ? formatValueWithOperator(value2, operator) : undefined,
        dataSourceType: 'device',
      };
    }

    default: {
      // Generic device attribute
      const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;
      const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;
      const formattedValue2 = value2 != null ? formatValueWithOperator(value2, operator) : undefined;

      return {
        type: 'device_attribute',
        name: field,
        operator: operatorLabel,
        value: formattedValue,
        value2: formattedValue2,
        dataSourceType: 'device',
      };
    }
  }
}

/**
 * Parse attribute conditions (person attributes)
 */
export function parseAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Extract field from various possible locations
  let field: string | undefined;
  let operator: string | undefined;
  let value: unknown;
  let value2: unknown;
  let inverse = data.inverse === true;

  // Check attribute_filter structure
  if ('attribute_filter' in data && data.attribute_filter != null) {
    const attrFilter = data.attribute_filter as Record<string, unknown>;
    field = (attrFilter.attribute ?? attrFilter.field) as string | undefined;
    operator = attrFilter.operator as string | undefined;
    value = attrFilter.value;
    value2 = attrFilter.value2;
    // Check for inverse flag inside attribute_filter
    if (attrFilter.inverse === true) {
      inverse = true;
    }
  } else {
    // Direct fields
    field = (data.attribute ?? data.field ?? data.name) as string | undefined;
    operator = data.operator as string | undefined;
    value = data.value;
    value2 = data.value2;
  }

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  // Format values - use timestamp-aware formatting for date operators
  const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;
  const formattedValue2 = value2 != null ? formatValueWithOperator(value2, operator) : undefined;

  return {
    type: 'attribute',
    name: field,
    operator: operatorLabel,
    value: formattedValue,
    value2: formattedValue2,
    dataSourceType: 'attribute',
    inverse,
  };
}

/**
 * Parse newsletter recipients conditions (type: 'newsletter_recipients')
 * Used in newsletter/broadcast recipient filtering
 */
export function parseNewsletterRecipientsCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'newsletter_recipients' && !('newsletter_recipients' in data)) return null;

  const inverse = data.inverse === true || data.negate === true;
  let newsletterId: number | string | undefined;

  // Handle { newsletter_recipients: { id: ... } } format
  if (
    'newsletter_recipients' in data &&
    typeof data.newsletter_recipients === 'object' &&
    data.newsletter_recipients != null
  ) {
    const nr = data.newsletter_recipients as Record<string, unknown>;
    newsletterId = nr.id as number | string | undefined;
  } else {
    newsletterId = data.newsletter_id as number | string | undefined;
  }

  // Check if newsletter name is available (from enriched data)
  const newsletterName = data.newsletter_name as string | undefined;

  return {
    type: 'newsletter_recipients',
    prefix: inverse ? 'is not a recipient of' : 'is a recipient of',
    name: newsletterName ?? (newsletterId != null ? `Newsletter #${newsletterId}` : 'newsletter'),
    dataSourceType: 'email',
  };
}

/**
 * Parse event attribute conditions (type: 'event_attribute')
 * Filters based on attributes of performed events
 */
export function parseEventAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'event_attribute') return null;

  const field = (data.attribute ?? data.field ?? data.name) as string | undefined;
  const operator = data.operator as string | undefined;
  const value = data.value;
  const inverse = data.inverse === true;

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  return {
    type: 'event_attribute',
    prefix: 'Event attribute',
    name: field,
    operator: operatorLabel,
    value: value != null ? formatValue(value) : undefined,
    dataSourceType: 'event',
  };
}

/**
 * Parse foreign event attribute conditions (type: 'foreign_event_attribute')
 * Filters based on attributes from events on related objects
 */
export function parseForeignEventAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'foreign_event_attribute') return null;

  const field = (data.attribute ?? data.field ?? data.name) as string | undefined;
  const operator = data.operator as string | undefined;
  const value = data.value;
  const inverse = data.inverse === true;

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  return {
    type: 'foreign_event_attribute',
    prefix: 'Related event attribute',
    name: field,
    operator: operatorLabel,
    value: value != null ? formatValue(value) : undefined,
    dataSourceType: 'event',
  };
}

/**
 * Parse object attribute conditions.
 *
 * Handles:
 * 1. type: 'object_attribute' - Used in object-triggered campaigns
 * 2. type: 5 with relationship_attr_filter - Nested filter conditions within a relationship condition
 *
 * Backend format for relationship_attr_filter:
 * {
 *   type: 5,
 *   relationship_attr_filter: {
 *     field: "phone",
 *     operator: "exists",
 *     type_id: 2
 *   }
 * }
 */
export function parseObjectAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Handle type: 4 (related object attribute) with related_object_attr_filter
  if (data.type === CONDITION_TYPE.RELATED || data.type === 4) {
    const attrFilter = data.related_object_attr_filter as Record<string, unknown> | undefined;
    if (attrFilter != null) {
      const field = attrFilter.field as string | undefined;
      const operator = attrFilter.operator as string | undefined;
      const value = attrFilter.value;
      const inverse = attrFilter.inverse === true;
      const typeId = attrFilter.type_id as number | string | undefined;

      // Get object type name from enrichment
      const objectTypeName = data.object_type_name as string | undefined;

      // Format operator label
      const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : 'is';

      // Format value - use timestamp-aware formatting for date operators
      const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;

      // Determine the attribute source label
      const sourceLabel = objectTypeName ?? (typeId != null ? `Object #${typeId}` : 'Object');

      return {
        type: 'object_attribute',
        prefix: `${sourceLabel} attribute`,
        name: field ?? 'attribute',
        operator: operatorLabel,
        value: formattedValue,
        dataSourceType: 'object',
      };
    }
  }

  // Handle type: 5 (CONDITION_TYPE.OBJECT_ATTRIBUTE) with relationship_attr_filter
  if (data.type === CONDITION_TYPE.OBJECT_ATTRIBUTE || data.type === 5) {
    const attrFilter = data.relationship_attr_filter as Record<string, unknown> | undefined;
    if (attrFilter != null) {
      const field = attrFilter.field as string | undefined;
      const operator = attrFilter.operator as string | undefined;
      const value = attrFilter.value;
      const inverse = attrFilter.inverse === true;
      const typeId = attrFilter.type_id as number | string | undefined;

      // Get object type name from enrichment
      const objectTypeName = data.object_type_name as string | undefined;

      // Format operator label
      const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : 'is';

      // Format value - use timestamp-aware formatting for date operators
      const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;

      // Determine the attribute source label
      const sourceLabel = objectTypeName ?? (typeId != null ? `Object #${typeId}` : 'Object');

      return {
        type: 'object_attribute',
        prefix: `${sourceLabel} attribute`,
        name: field ?? 'attribute',
        operator: operatorLabel,
        value: formattedValue,
        dataSourceType: 'object',
      };
    }
  }

  // Handle direct related_object_attr_filter without type: 4
  if (
    'related_object_attr_filter' in data &&
    typeof data.related_object_attr_filter === 'object' &&
    data.related_object_attr_filter != null
  ) {
    const attrFilter = data.related_object_attr_filter as Record<string, unknown>;
    const field = attrFilter.field as string | undefined;
    const operator = attrFilter.operator as string | undefined;
    const value = attrFilter.value;
    const inverse = attrFilter.inverse === true;
    const typeId = attrFilter.type_id as number | string | undefined;

    const objectTypeName = data.object_type_name as string | undefined;
    const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : 'is';
    // Use timestamp-aware formatting for date operators
    const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;
    const sourceLabel = objectTypeName ?? (typeId != null ? `Object #${typeId}` : 'Object');

    return {
      type: 'object_attribute',
      prefix: `${sourceLabel} attribute`,
      name: field ?? 'attribute',
      operator: operatorLabel,
      value: formattedValue,
      dataSourceType: 'object',
    };
  }

  // Handle direct relationship_attr_filter without type: 5
  if (
    'relationship_attr_filter' in data &&
    typeof data.relationship_attr_filter === 'object' &&
    data.relationship_attr_filter != null
  ) {
    const attrFilter = data.relationship_attr_filter as Record<string, unknown>;
    const field = attrFilter.field as string | undefined;
    const operator = attrFilter.operator as string | undefined;
    const value = attrFilter.value;
    const inverse = attrFilter.inverse === true;
    const typeId = attrFilter.type_id as number | string | undefined;

    const objectTypeName = data.object_type_name as string | undefined;
    const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : 'is';
    // Use timestamp-aware formatting for date operators
    const formattedValue = value != null ? formatValueWithOperator(value, operator) : undefined;
    const sourceLabel = objectTypeName ?? (typeId != null ? `Object #${typeId}` : 'Object');

    return {
      type: 'object_attribute',
      prefix: `${sourceLabel} attribute`,
      name: field ?? 'attribute',
      operator: operatorLabel,
      value: formattedValue,
      dataSourceType: 'object',
    };
  }

  // Handle type: 'object_attribute' - Used in object-triggered campaigns
  if (data.type !== 'object_attribute') return null;

  const field = (data.attribute ?? data.field ?? data.name) as string | undefined;
  const operator = data.operator as string | undefined;
  const value = data.value;
  const inverse = data.inverse === true;

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  return {
    type: 'object_attribute',
    prefix: 'Object attribute',
    name: field,
    operator: operatorLabel,
    // Use timestamp-aware formatting for date operators
    value: value != null ? formatValueWithOperator(value, operator) : undefined,
    dataSourceType: 'object',
  };
}

/**
 * Parse collection attribute conditions (type: 'collection_attribute')
 * Filters based on collection attributes
 */
export function parseCollectionAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'collection_attribute') return null;

  const field = (data.attribute ?? data.field ?? data.name) as string | undefined;
  const operator = data.operator as string | undefined;
  const value = data.value;
  const inverse = data.inverse === true;

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  return {
    type: 'collection_attribute',
    prefix: 'Collection attribute',
    name: field,
    operator: operatorLabel,
    value: value != null ? formatValue(value) : undefined,
    dataSourceType: 'value',
  };
}

/**
 * Parse inbound attribute conditions (type: 'inbound_attribute')
 * Filters based on attributes of inbound messages
 */
export function parseInboundAttributeCondition(data: Record<string, unknown>): ParsedCondition | null {
  if (data.type !== 'inbound_attribute') return null;

  const field = (data.attribute ?? data.field ?? data.name) as string | undefined;
  const operator = data.operator as string | undefined;
  const value = data.value;
  const inverse = data.inverse === true;

  if (field == null) return null;

  const operatorLabel = operator != null ? getOperatorLabel(operator, inverse) : undefined;

  return {
    type: 'inbound_attribute',
    prefix: 'Inbound message attribute',
    name: field,
    operator: operatorLabel,
    value: value != null ? formatValue(value) : undefined,
    dataSourceType: 'inbound',
  };
}

/**
 * Message action labels matching ~/code/ui/app/utils/email-filters.ts
 * Uses "person_subject_present_perfect" format: "people who have been sent any email"
 * Source: NON_SUBJECT_EMAIL_FILTERS which uses person_subject_present_perfect
 */
const MessageActionLabels: Record<string, string> = {
  // Email actions
  sent_email: 'been sent',
  delivered_email: 'been delivered',
  opened_email: 'opened',
  clicked_email: 'clicked',
  dropped_email: 'suppressed', // Changed from 'been suppressed' to match UI
  converted_email: 'converted',
  bounced_email: 'bounced',
  failed_email: 'failed to receive',
  spammed_email: 'spammed',
  unsubscribed_email: 'unsubscribed',
  // Push actions
  sent_push: 'been sent',
  delivered_push: 'been delivered',
  opened_push: 'opened',
  clicked_push: 'clicked',
  bounced_push: 'bounced',
  failed_push: 'failed to receive',
  // SMS (Twilio) actions
  sent_twilio: 'been sent',
  delivered_twilio: 'been delivered',
  clicked_twilio: 'clicked',
  bounced_twilio: 'bounced',
  failed_twilio: 'failed to receive',
  // In-App actions
  sent_in_app: 'been sent',
  opened_in_app: 'opened',
  clicked_in_app: 'clicked',
  converted_in_app: 'converted',
  failed_in_app: 'failed to receive',
  // Slack actions
  sent_slack: 'been sent',
  converted_slack: 'converted',
  failed_slack: 'failed to send',
  // Webhook actions
  sent_webhook: 'been sent',
  clicked_webhook: 'clicked',
  converted_webhook: 'converted',
  failed_webhook: 'failed to send',
};

/**
 * Channel type labels for message conditions
 */
const ChannelTypeLabels: Record<string, string> = {
  email: 'email',
  push: 'push notification',
  twilio: 'SMS',
  in_app: 'in-app message',
  slack: 'Slack message',
  webhook: 'webhook',
};

/**
 * Parse message interaction conditions (from event data with message types)
 * Formats like UI: "have ever been sent any email" or "have not ever opened the email [name]"
 */
export function parseMessageInteractionCondition(data: Record<string, unknown>): ParsedCondition | null {
  const eventData = data.event as Record<string, unknown> | undefined;
  const eventType = (eventData?.type ?? eventData?.ui_type) as string | undefined;
  const inverse = data.inverse === true;

  const messageEventTypes = [
    'sent_email',
    'opened_email',
    'clicked_email',
    'bounced_email',
    'delivered_email',
    'dropped_email',
    'converted_email',
    'failed_email',
    'spammed_email',
    'unsubscribed_email',
    'sent_push',
    'opened_push',
    'clicked_push',
    'bounced_push',
    'delivered_push',
    'failed_push',
    'sent_twilio',
    'delivered_twilio',
    'clicked_twilio',
    'bounced_twilio',
    'failed_twilio',
    'sent_in_app',
    'opened_in_app',
    'clicked_in_app',
    'converted_in_app',
    'failed_in_app',
    'sent_slack',
    'converted_slack',
    'failed_slack',
    'sent_webhook',
    'clicked_webhook',
    'converted_webhook',
    'failed_webhook',
    // Generic action types (detect channel from message name)
    'sent_action',
    'delivered_action',
    'opened_action',
    'clicked_action',
    'bounced_action',
    'converted_action',
    'failed_action',
    'dropped_action',
  ];

  if (eventType == null || !messageEventTypes.includes(eventType)) {
    return null;
  }

  // Get the action label (e.g., "been sent", "opened", "clicked")
  // For generic _action types, map to the base action
  let actionLabel: string;
  if (eventType.endsWith('_action')) {
    const baseAction = eventType.replace('_action', '');
    // Map to email action labels for consistency
    actionLabel = MessageActionLabels[`${baseAction}_email`] ?? baseAction;
  } else {
    actionLabel = MessageActionLabels[eventType] ?? eventType;
  }

  // Get the channel from event type (e.g., "email" from "sent_email")
  // For generic _action types, detect channel from message name
  let channel: string;
  let channelLabel: string;
  const channelMatch = eventType.match(/_(email|push|twilio|in_app|slack|webhook|action)$/);

  if (eventType.endsWith('_action')) {
    // For generic action types, detect channel from message name
    const eventName = eventData?.name as string | undefined;
    if (eventName != null) {
      if (eventName.includes('webhook') || eventName.startsWith('webhook_')) {
        channel = 'webhook';
        channelLabel = ChannelTypeLabels.webhook ?? 'webhook';
      } else if (eventName.includes('twilio') || eventName.includes('sms')) {
        channel = 'twilio';
        channelLabel = ChannelTypeLabels.twilio ?? 'SMS';
      } else if (eventName.includes('slack')) {
        channel = 'slack';
        channelLabel = ChannelTypeLabels.slack ?? 'Slack message';
      } else if (eventName.includes('in_app') || eventName.includes('in-app')) {
        channel = 'in_app';
        channelLabel = ChannelTypeLabels.in_app ?? 'in-app message';
      } else if (eventName.includes('push')) {
        channel = 'push';
        channelLabel = ChannelTypeLabels.push ?? 'push notification';
      } else {
        // Default to email for action types
        channel = 'email';
        channelLabel = ChannelTypeLabels.email ?? 'email';
      }
    } else {
      channel = 'message';
      channelLabel = 'message';
    }
  } else {
    channel = channelMatch?.[1] ?? 'message';
    channelLabel = ChannelTypeLabels[channel] ?? channel;
  }

  // Check for campaign name from enriched data (from enrichConditionWithCampaignName)
  // It can be in the event object or at the top level
  const campaignName = (eventData?.campaign_name as string | undefined) ?? (data.campaign_name as string | undefined);

  // Check if it's "any" message (event.name === '*' or wildcard pattern like "webhook_*")
  const eventName = eventData?.name as string | undefined;
  const isAny = eventName === '*' || eventName === '*_h'; // *_h is for human opens/clicks
  const isWildcardPattern = eventName != null && eventName.endsWith('_*');

  // Build the display name
  // Format: "any email" or "the email [name]" or "any webhook" for wildcards
  let displayName: string;
  if (isAny) {
    displayName = `any ${channelLabel}`;
  } else if (isWildcardPattern) {
    // For wildcard patterns like "webhook_*", show "any webhook"
    // eventName is guaranteed to be defined when isWildcardPattern is true
    const channelPrefix = (eventName ?? '').replace('_*', '');
    const wildcardChannelLabel = ChannelTypeLabels[channelPrefix] ?? channelPrefix;
    displayName = `any ${wildcardChannelLabel}`;
  } else {
    const messageName = eventName ?? (eventData?.action as string | undefined);
    // Check for enriched message name (from enrichConditionWithMessageName)
    const enrichedMessageName =
      (eventData?.message_name as string | undefined) ?? (data.message_name as string | undefined);

    if (enrichedMessageName != null && enrichedMessageName !== '') {
      // Use the enriched message name (e.g., "Email 1" instead of "output_5")
      displayName = `the ${channelLabel} ${enrichedMessageName}`;
    } else if (messageName != null) {
      displayName = `the ${channelLabel} ${messageName}`;
    } else {
      displayName = `any ${channelLabel}`;
    }
  }

  // Determine data source type based on channel
  let dataSourceType: DataSourceType = 'message';
  if (channel === 'email') {
    dataSourceType = 'email';
  } else if (channel === 'push') {
    dataSourceType = 'push';
  } else if (channel === 'twilio') {
    dataSourceType = 'sms';
  } else if (channel === 'in_app') {
    dataSourceType = 'in_app';
  } else if (channel === 'slack') {
    dataSourceType = 'slack';
  } else if (channel === 'webhook') {
    dataSourceType = 'webhook';
  }

  // Build prefix and operator separately to match UI structure
  // UI format: "have ever" (prefix) + "suppressed" (operator) + "the email Email 1" (name) + "in Campaign Name" (value)
  // For clicked_action, use special format: "have ever clicked on any tracked link in"
  let prefix: string;
  let operator: string | undefined;

  if (eventType === 'clicked_action' || eventType === 'clicked_webhook') {
    // Special case: clicked actions have the full phrase in prefix
    prefix = inverse ? 'have not ever clicked on any tracked link in' : 'have ever clicked on any tracked link in';
    operator = undefined;
  } else {
    // Standard format: separate prefix and operator
    prefix = inverse ? 'have not ever' : 'have ever';
    operator = actionLabel;
  }

  // Get icon name for message based on dataSourceType
  const messageIcon = DATA_SOURCE_TYPE_ICON[dataSourceType] ?? 'email';

  // Determine campaign icon based on campaign type
  // For now, default to 'campaigns' - can be enhanced later to check campaign type
  // (newsletter -> 'newsletter', transactional -> 'transactional-api', campaign -> 'campaigns')
  const campaignIcon = 'campaigns'; // TODO: Enhance to check actual campaign type if available

  return {
    type: 'message',
    prefix,
    operator,
    name: displayName,
    value: campaignName != null ? `in ${campaignName}` : undefined,
    dataSourceType,
    messageIcon,
    campaignIcon: campaignName != null ? campaignIcon : undefined,
    channelLabel, // Store channel label for parsing display name
    inverse,
  };
}

// =============================================================================
// Main Parser
// =============================================================================

/**
 * Parse condition data and return a structured result for display.
 * Tries all parsers in order of specificity.
 *
 * Supports all condition types from ~/code/ui/app/utils/condition-types.ts:
 * - attribute, event, segment, device_attribute, email, form, pageview, screenview
 * - inbound, opt_out, relationship, message (actions)
 * - newsletter_recipients, event_attribute, foreign_event_attribute
 * - object_attribute, collection_attribute, inbound_attribute
 */
export function parseCondition(data: Record<string, unknown>): ParsedCondition | null {
  // Fast-path based on type
  const conditionType = data.type;

  if (conditionType === 'email') {
    return parseEmailCondition(data);
  }
  if (conditionType === CONDITION_TYPE.ATTRIBUTE || conditionType === String(CONDITION_TYPE.ATTRIBUTE)) {
    return parseAttributeCondition(data);
  }
  if (conditionType === CONDITION_TYPE.EVENT || conditionType === String(CONDITION_TYPE.EVENT)) {
    return parseEventCondition(data);
  }
  if (conditionType === CONDITION_TYPE.DEVICE || conditionType === String(CONDITION_TYPE.DEVICE)) {
    return parseDeviceAttributeCondition(data);
  }
  if (typeof conditionType === 'string' && (conditionType.endsWith('_email') || conditionType.endsWith('_action'))) {
    return parseMessageActionCondition(data);
  }
  if (conditionType === 'pageview') {
    return parsePageviewCondition(data);
  }
  if (conditionType === 'screenview') {
    return parseScreenviewCondition(data);
  }
  if (conditionType === 'form') {
    return parseFormCondition(data);
  }
  // Handle type: 'message' conditions (e.g., newsletter recipients with event: { type: 'sent_email' })
  if (conditionType === 'message') {
    return parseMessageInteractionCondition(data);
  }
  if (conditionType === 'inbound') {
    return parseInboundCondition(data);
  }
  if (conditionType === 'inbound_attribute') {
    return parseInboundAttributeCondition(data);
  }
  if (conditionType === 'opt_out') {
    return parseOptOutCondition(data);
  }
  if (
    conditionType === 'relationship' ||
    conditionType === 'related' ||
    conditionType === CONDITION_TYPE.RELATIONSHIP ||
    conditionType === 3
  ) {
    return parseRelationshipCondition(data);
  }
  if (
    conditionType === CONDITION_TYPE.OBJECT_ATTRIBUTE ||
    conditionType === 5 ||
    conditionType === CONDITION_TYPE.RELATED ||
    conditionType === 4 ||
    'relationship_attr_filter' in data ||
    'related_object_attr_filter' in data
  ) {
    return parseObjectAttributeCondition(data);
  }
  if (conditionType === 'segment' || conditionType === 'segments' || conditionType === 'segments_negatable') {
    return parseSegmentCondition(data);
  }
  if (conditionType === 'device_attribute' || conditionType === 'device_change') {
    return parseDeviceAttributeCondition(data);
  }
  if (conditionType === 'newsletter_recipients') {
    return parseNewsletterRecipientsCondition(data);
  }
  if (conditionType === 'event_attribute') {
    return parseEventAttributeCondition(data);
  }
  if (conditionType === 'foreign_event_attribute') {
    return parseForeignEventAttributeCondition(data);
  }
  if (conditionType === 'object_attribute') {
    return parseObjectAttributeCondition(data);
  }
  if (conditionType === 'collection_attribute') {
    return parseCollectionAttributeCondition(data);
  }

  // Fast-path for specific filter types
  if ('event_filter' in data) {
    return parseEventCondition(data);
  }
  if ('attribute_filter' in data) {
    return parseAttributeCondition(data);
  }
  if ('newsletter_recipients' in data) {
    return parseNewsletterRecipientsCondition(data);
  }
  if ('relationship' in data || 'relationship_filter' in data) {
    return parseRelationshipCondition(data);
  }

  // Fallback: Try each parser in order of specificity
  return (
    parseEmailCondition(data) ??
    parseMessageActionCondition(data) ??
    parseNewsletterRecipientsCondition(data) ??
    parseSegmentCondition(data) ??
    parseEventCondition(data) ??
    parseEventAttributeCondition(data) ??
    parseForeignEventAttributeCondition(data) ??
    parsePageviewCondition(data) ??
    parseScreenviewCondition(data) ??
    parseFormCondition(data) ??
    parseMessageInteractionCondition(data) ??
    parseDeviceAttributeCondition(data) ??
    parseObjectAttributeCondition(data) ??
    parseCollectionAttributeCondition(data) ??
    parseInboundCondition(data) ??
    parseInboundAttributeCondition(data) ??
    parseOptOutCondition(data) ??
    parseRelationshipCondition(data) ??
    parseAttributeCondition(data)
  );
}
